import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Divider, TextField } from "@mui/material";
import { useTranslations } from "use-intl";
import { type FormikProps } from "formik";
import {
  CircleNotchIcon,
  MapPinIcon,
  MapPinLineIcon,
} from "@phosphor-icons/react";
import { type THotelFormValues } from "@/app/lib/types/types";
import { useHotelsCitiesSearch } from "@/app/lib/hooks/useHotelsCitiesSearch";
import { usePreviousCitySearches } from "@/app/lib/hooks/usePreviousSearches";
import { initialHotelsValues } from "@/app/lib/initialHotelsValues";
import type { FindCitiesCity } from "@/app/lib/types/codegen";
import { SearchResultList, SearchResultButton } from "../SearchResultList";
import { LastSearchedCityList } from "../LastSearchedCityList";
import { TopLocationList } from "../TopLocationList";
import { topDestinationsQueryOptions } from "@/app/lib/hooks/useTopLocations";
import { ResetButton } from "../ResetButton";

function DestinationSearch<T extends Pick<THotelFormValues, "destination">>({
  onClose,
  formProps,
}: {
  onClose: () => void;
  formProps: FormikProps<T>;
}) {
  const t = useTranslations();
  const { previousSearches, addPreviousSearch } = usePreviousCitySearches();
  const { data: topLocations } = useQuery(topDestinationsQueryOptions());
  const [input, setInput] = useState(formProps.values.destination?.name || "");
  const [isDirty, setIsDirty] = useState(
    input !== formProps.values.destination?.name,
  );
  const inputRef = useRef<HTMLInputElement>(null);

  const { data, isFetching, isSuccess } = useHotelsCitiesSearch(
    isDirty ? input : "",
  );

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.currentTarget.value.slice(0, 30);
    setIsDirty(true);
    setInput(input);
  };

  const handleClick = async (value: FindCitiesCity) => {
    await formProps.setFieldValue("destination", value);
    onClose();
  };

  const resetDestination = async () => {
    await formProps.setFieldValue(
      "destination",
      initialHotelsValues.destination,
    );
    setInput("");
    inputRef.current?.focus();
  };

  useEffect(() => {
    // select input text on first render
    inputRef.current?.select();
  }, []);

  const hasPreviousSearches = previousSearches.length > 0;

  return (
    <div className="w-full">
      <div className="relative flex w-full flex-col gap-2">
        <TextField
          name="destination"
          autoFocus
          fullWidth
          value={input}
          onChange={onChange}
          slotProps={{
            input: {
              inputRef,
              endAdornment: formProps.values.destination ? (
                <ResetButton
                  onClick={resetDestination}
                  aria-label={t("hotels.search.resetDestination")}
                />
              ) : null,
            },
          }}
          onBlur={formProps.handleBlur}
          label={
            <div className="flex items-center gap-2">
              <MapPinLineIcon size={24} />
              <span>{t("hotels.search.destination")}</span>
            </div>
          }
        />
      </div>
      {isFetching ? (
        <div className="mt-6 flex justify-center text-sm text-text-placeholder">
          <CircleNotchIcon className="animate-spin" size={30} />
        </div>
      ) : isSuccess ? (
        data?.length > 0 ? (
          <SearchResultList
            data={data}
            getItemKey={(city) => city.id}
            renderItem={(city) => (
              <SearchResultButton
                icon={MapPinIcon}
                primaryText={city.name}
                secondaryText={city.countryName}
                onClick={() => {
                  addPreviousSearch(city);
                  handleClick(city);
                }}
              />
            )}
          />
        ) : (
          <div className="mt-6 text-center">
            <div className="mb-1 text-base font-medium">
              {t("hotels.search.nothingFound")}
            </div>
            <div className="text-sm text-text-placeholder">
              {t("hotels.search.checkInput")}
            </div>
          </div>
        )
      ) : (
        <>
          {hasPreviousSearches ? (
            <LastSearchedCityList onClick={handleClick} />
          ) : null}

          {topLocations?.length ? (
            <>
              {hasPreviousSearches ? (
                <Divider
                  sx={{
                    mt: "calc(var(--spacing) * 4)",
                    borderColor: "var(--color-stroke-neutral-soft)",
                  }}
                />
              ) : null}
              <TopLocationList
                topLocations={topLocations}
                onClick={handleClick}
              />
            </>
          ) : null}
        </>
      )}
    </div>
  );
}

export default DestinationSearch;
